import {
  AppBar,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { FC, useEffect, useState } from "react";
import LogoNavBar from "../icons/logo-Ummia.png";
import ProfileMenu from "./ProfileMenu";
import { Link } from "react-router-dom";
import { useAuthContext } from "../../../hooks/Auth";
import { iUser } from "../../../interfaces/Auth/iUser";
import { environment } from "../../../config";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";

import UmmIACoin from "../../../components/UmmIAChatAI/svg/UmmIACoin.svg";
import CreditosNavBar from "../../../components/Creditos/CreditosNavBar";

interface Props {
  handleDrawerToggle: () => void;
}

const { REACT_APP_API_CONTROL_TRANSACCIONAL, ENVIRONMENT, VERSION } = environment;
const baseApiBroker = REACT_APP_API_CONTROL_TRANSACCIONAL;
const proyectVersion = VERSION;

function extractOrganizacion(data: any): string | null {
  if (!data) return null;

  if (typeof data.organizacion === "string" && data.organizacion.trim() !== "") {
    return data.organizacion.trim();
  }

  if (
    data.body &&
    typeof data.body.organizacion === "string" &&
    data.body.organizacion.trim() !== ""
  ) {
    return data.body.organizacion.trim();
  }

  if (
    Array.isArray(data.body) &&
    data.body.length > 0 &&
    typeof data.body[0].organizacion === "string" &&
    data.body[0].organizacion.trim() !== ""
  ) {
    return data.body[0].organizacion.trim();
  }

  if (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0].organizacion === "string" &&
    data[0].organizacion.trim() !== ""
  ) {
    return data[0].organizacion.trim();
  }


  return null;
}

const Navbar: FC<Props> = ({ handleDrawerToggle }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const [organizacion, setOrganizacion] = useState<string | null>(null);
  const [loadingOrganizacion, setLoadingOrganizacion] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const { logout, canAccess, user } = useAuthContext();



  useEffect(() => {
    const idOrganizacion = user?.id_organizacion;
    if (!idOrganizacion) {
      setOrganizacion(null);
      setOrgError(null);
      setLoadingOrganizacion(false);
      return;
    }


    if (!baseApiBroker) {
      setOrgError("API base no configurada");
      setOrganizacion(null);
      return;
    }

    if (!proyectVersion) {
      setOrgError("Versión de proyecto no configurada");
      setOrganizacion(null);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchOrganizacion = async () => {
      setLoadingOrganizacion(true);
      setOrgError(null);

      try {
        const base = String(baseApiBroker).replace(/\/$/, "");

        const url = `${base}/creditos-organizacion?id_organizacion=${encodeURIComponent(
          idOrganizacion
        )}&version=${encodeURIComponent(proyectVersion)}`;


        if (process.env.NODE_ENV === "development") {

          console.debug("[ProfileModal] Fetch URL:", url);
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",

          },
          signal,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();


        if (process.env.NODE_ENV === "development") {

          console.debug("[ProfileModal] API response:", data);
        }

        const extracted = extractOrganizacion(data);

        if (extracted) {
          setOrganizacion(extracted);
        } else {

          setOrganizacion(null);
          setOrgError("Campo 'organizacion' no disponible en la respuesta");
        }
      } catch (err: any) {
        if (err.name === "AbortError") {

          return;
        }
        setOrganizacion(null);
        setOrgError(err?.message ?? "Error al obtener organización");
      } finally {
        setLoadingOrganizacion(false);
      }
    };

    fetchOrganizacion();
    return () => {
      controller.abort();
    };
  }, [user?.id_organizacion, baseApiBroker, proyectVersion]);


  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "#FCFCFC",
        color: "#2f2f2f",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.5)",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Boton para abrir el menú en mobile */}
          {isMobile && (
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, color: "#336B87" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo UmmIA */}
          <Link to="/asistente/menu">
            <Box
              component="img"
              src={LogoNavBar}
              alt="Logo"
              sx={{
                height: { xs: 35, md: 40, lg: 45 },
                width: { xs: 120, md: 130, lg: 140 },
                objectFit: "fit",
                cursor: "pointer",
                display: "flex",
              }}
            />
          </Link>
        </Box>

        {/* Menú de perfil */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Stack direction="row" spacing={1} >
            <Typography fontFamily={"Montserrat"} fontSize={12} >Organización:</Typography>
            <Typography fontWeight="bold" fontSize={12} fontFamily={"Montserrat"} color={"#336B87"}>
              {loadingOrganizacion
                ? "Cargando..."
                : organizacion ?? user?.id_organizacion ?? ""}
            </Typography>
          </Stack>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 2, borderColor: "#ccc", my: 1}}
          />

          {/** Contenedor de Créditos */}
          <CreditosNavBar/>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 2, borderColor: "#ccc", my: 1}}
          />

          <ProfileMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
